import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { GameState } from '../types'
import Card from './Card'
import { Language } from '../data/translations'
import { getPreparedCardAnchorId, uiAnchorRegistry } from '../services/uiAnchorRegistry'

// Vertical offset for counter chains to not cover attacking card (allows targeting first card)
const CHAIN_VERTICAL_OFFSET = 70

// Type for activation zone data exposed for touch hit-testing
export interface ActivationZone {
  activationId: string;
  rect: DOMRect;
}

const ActivationChainLayer: React.FC<{
  gameState: GameState
  isMyTurnToRespond: boolean
  localPlayerId: number
  preparedCardRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>
  onDropOnActivationChain: (e: React.DragEvent<HTMLDivElement>, targetActivationId?: string) => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnter: () => void
  onDragLeave: () => void
  isTouchDragging?: boolean
  // Ref to expose activation zone rects for touch hit-testing
  activationZonesRef?: React.MutableRefObject<ActivationZone[]>
  language?: Language
}> = ({ gameState, isMyTurnToRespond, localPlayerId, preparedCardRefs, onDropOnActivationChain, onDragOver, onDragEnter, onDragLeave, isTouchDragging = false, activationZonesRef, language = 'en' }) => {
  // Local hover state for each activation - tracks which activation is being hovered
  const [hoveredActivationId, setHoveredActivationId] = useState<string | null>(null)
  const getPreparedAnchorRect = (cardId: string) => uiAnchorRegistry.getRect(getPreparedCardAnchorId(cardId)) ?? preparedCardRefs.current[cardId]?.getBoundingClientRect() ?? null

  // Update activation zones ref for touch hit-testing
  useEffect(() => {
    if (!activationZonesRef) return;
    const zones: ActivationZone[] = [];
    gameState.activations.forEach((activation, activationIndex) => {
      if (activation.accepted || activation.resolved) return;
      const anchorCardId = activation.attackingCard.card.id;
      const anchorRect = getPreparedAnchorRect(anchorCardId);
      if (anchorRect) {

        // 1. Add attacking card zone
        zones.push({ activationId: anchorCardId, rect: anchorRect });

        // 2. Add counter chain card zones
        if (activation.counterChain && activation.counterChain.length > 0) {
          activation.counterChain.forEach((_, index) => {
            const xOffset = activationIndex * 15;
            const yOffset = (index % 2 === 0 ? 1 : -1) * (Math.floor(index / 2) + 1) * 8 + (activationIndex * 15);

            // Calculate position relative to viewport (using same logic as rendering)
            const top = anchorRect.top + CHAIN_VERTICAL_OFFSET + yOffset;
            const left = anchorRect.left + xOffset;

            zones.push({
              activationId: anchorCardId,
              rect: new DOMRect(left, top, anchorRect.width, anchorRect.height)
            });
          });
        }
      }
    });
    activationZonesRef.current = zones;
  }, [gameState.activations, preparedCardRefs, activationZonesRef, isMyTurnToRespond]);

  const pointerEventsClass = isTouchDragging ? 'pointer-events-auto' : 'pointer-events-none'
  const portalRoot = document.getElementById('portal-root')
  if (!portalRoot) return null
  const portals = gameState.activations.map((activation, activationIndex) => {
    // PHASE 5: Can drop on any UNACCEPTED activation where:
    // 1. It's globally my turn (opponent played the last card somewhere)
    // 2. This specific activation is not accepted/resolved
    // 3. The last card in THIS specific chain belongs to opponent (per-chain check)
    const isActivationAccepted = activation.accepted || false
    const isActivationResolved = activation.resolved || false

    // Per-chain check: who played the last card in THIS chain?
    const lastCardInThisChain = activation.counterChain.length > 0
      ? activation.counterChain[activation.counterChain.length - 1]
      : activation.attackingCard;
    // Can only respond to chains where the last card belongs to opponent
    const lastCardInChainIsOpponents = lastCardInThisChain.card.ownerId !== localPlayerId;

    const canDropOnThisActivation = isMyTurnToRespond && !isActivationAccepted && !isActivationResolved && lastCardInChainIsOpponents

    const anchorCardId = activation.attackingCard.card.id
    const rect = getPreparedAnchorRect(anchorCardId)
    if (!rect) return null

    const isHovered = hoveredActivationId === anchorCardId
    // Show highlighted state when hovered OR when touch dragging
    const showHighlight = isHovered || isTouchDragging
    // 15% zoom effect when highlighted
    const zoomScale = showHighlight ? 1.15 : 1

    // Handlers for this specific activation
    const handleDragEnter = () => {
      setHoveredActivationId(anchorCardId)
      onDragEnter()
    }
    const handleDragLeave = () => {
      setHoveredActivationId(null)
      onDragLeave()
    }

    // If chain is empty, render drop zone on attacking card
    if (!activation.counterChain || activation.counterChain.length === 0) {
      if (!canDropOnThisActivation) return null
      return ReactDOM.createPortal(
        <div
          key={`activation-empty-${activationIndex}`}
          className={`fixed z-30 ${pointerEventsClass} transition-transform duration-150`}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            transform: `scale(${zoomScale})`,
            transformOrigin: 'center center'
          }}
          onDrop={(e) => onDropOnActivationChain(e, anchorCardId)}
          onDragOver={onDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <div className={`w-full h-full border-2 rounded-lg transition-all duration-150 ${showHighlight
            ? 'border-green-400 bg-green-400/30 shadow-lg shadow-green-400/50'
            : 'border-yellow-400/50 bg-yellow-400/10 animate-pulse'
            }`} />
        </div>,
        portalRoot
      )
    }

    // Counter chain exists - render BOTH:
    // 1. Drop zone on attacking card (for extended shields targeting first card)
    // 2. Counter chain cards shifted down with drop zone on last card
    const attackingCardDropZone = canDropOnThisActivation ? ReactDOM.createPortal(
      <div
        key={`activation-attacking-${activationIndex}`}
        className={`fixed z-29 ${pointerEventsClass} transition-transform duration-150`}
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          transform: `scale(${showHighlight ? 1.15 : 1})`,
          transformOrigin: 'center center'
        }}
        onDrop={(e) => onDropOnActivationChain(e, anchorCardId)}
        onDragOver={onDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <div className={`w-full h-full border-2 rounded-lg transition-all duration-150 ${showHighlight
          ? 'border-green-400 bg-green-400/30 shadow-lg shadow-green-400/50'
          : 'border-yellow-400/30 bg-transparent'
          }`} />
      </div>,
      portalRoot
    ) : null

    const counterChainPortal = ReactDOM.createPortal(
      <div
        key={`activation-chain-${activationIndex}`}
        className="pointer-events-none transition-transform duration-150"
        style={{
          position: 'fixed',
          top: rect.top + CHAIN_VERTICAL_OFFSET, // Shift down to not cover attacking card
          left: rect.left,
          zIndex: 30 + activationIndex,
          transform: `scale(${zoomScale})`,
          transformOrigin: 'top left'
        }}
      >
        {activation.counterChain.map((pCard, index) => {
          const xOffset = activationIndex * 15
          // Zigzag pattern for cards in chain
          const yOffset = (index % 2 === 0 ? 1 : -1) * (Math.floor(index / 2) + 1) * 8 + (activationIndex * 15)
          const isLastCardInChain = index === activation.counterChain.length - 1
          return (
            <div key={`chain-${activationIndex}-${pCard.card.id}`} className="absolute" style={{ transform: `translate(${xOffset}px, ${yOffset}px)` }}>
              <Card card={pCard.card} rotation={pCard.rotation} language={language} />
              {isLastCardInChain && canDropOnThisActivation && (
                <div
                  className={`absolute -inset-2 z-10 ${pointerEventsClass} border-2 rounded-lg transition-all duration-150 ${showHighlight
                    ? 'border-green-400 shadow-lg shadow-green-400/50'
                    : 'border-transparent'
                    }`}
                  onDrop={(e) => onDropOnActivationChain(e, anchorCardId)}
                  onDragOver={onDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                />
              )}
            </div>
          )
        })}
      </div>,
      portalRoot
    )

    return [attackingCardDropZone, counterChainPortal]
  }).flat().filter(Boolean) as React.ReactNode[]
  return <>{portals}</>
}

export default ActivationChainLayer
